import type { SaveFirmAccountantPayload } from '@/core/models/firm-settings.models';
import type { InputCharMode } from '@/kit/inputCharMode';

/**
 * The firm accountant form (Settings > Accountants), shared by the Add/Edit Accountant page and the Accountant's
 * Report "Add new accountant" dialog so both validate and save an accountant the same way.
 */
export interface AccountantForm {
  name: string;
  addressLine: string;
  addressLine2: string;
  city: string;
  postCode: string;
  county: string;
  country: string | null;
  accountingBody: string | null;
  accountingBodyName: string;
}

export type AccountantTextField =
  'name' | 'addressLine' | 'addressLine2' | 'city' | 'postCode' | 'county' | 'accountingBodyName';

/**
 * The accounting body value that triggers the free-text "Accounting body name" field.
 * The API may return 'Other' or 'Other/None'; we treat either as the trigger.
 */
export const ACCOUNTING_BODY_OTHER = 'Other/None';

/** Returns true if the given accounting body value should show the free-text name field. */
export function isOtherAccountingBody(value: string | null): boolean {
  return value === 'Other/None' || value === 'Other';
}

/**
 * BUG-015: the accounting body choices - the API's list, plus the accountant's saved body when it is no longer listed
 * (CAI, CIOT and ATT were offered before the list followed 3.0's), so a saved value is shown rather than blanked.
 */
/** BUG-015: bodies an earlier build offered that 3.0's list does not have; the API still accepts a saved one. */
const RETIRED_ACCOUNTING_BODIES = ['CAI', 'CIOT', 'ATT'];

/** Whether the API accepts the accounting body: one of its list, or a retired one an accountant may still hold. */
export function isKnownAccountingBody(listed: readonly string[], body: string): boolean {
  return listed.includes(body) || RETIRED_ACCOUNTING_BODIES.includes(body);
}

export function accountingBodyItems(listed: readonly string[], current: string | null): string[] {
  const saved = current?.trim();
  if (!saved || isOtherAccountingBody(saved)) return [...listed];
  return listed.includes(saved) ? [...listed] : [...listed, saved];
}

export const NEW_ACCOUNTANT_FORM: AccountantForm = {
  name: '',
  addressLine: '',
  addressLine2: '',
  city: '',
  postCode: '',
  county: '',
  country: 'United Kingdom',
  accountingBody: null,
  accountingBodyName: '',
};

/** Validators: name required + maxLength(255); address lines 500; city/county 100; post code 20; body name 20. */
export const ACCOUNTANT_MAX_LENGTH: Record<AccountantTextField, number> = {
  name: 255,
  addressLine: 500,
  addressLine2: 500,
  city: 100,
  postCode: 20,
  county: 100,
  accountingBodyName: 20,
};

/**
 * AP parity (BUG-013): the old app imposes no character-level filtering — only maxLength.
 * All fields are left unrestricted so firm names like "Smith & Co 123 (UK)" and addresses
 * like "12 High St, #4" are accepted exactly as typed.
 */
export const ACCOUNTANT_CHAR_MODE: Partial<Record<AccountantTextField, InputCharMode>> = {};
export const ACCOUNTANT_EXTRA_CHARS: Partial<Record<AccountantTextField, string>> = {};

export const ACCOUNTANT_TEXT_FIELDS: AccountantTextField[] = [
  'name',
  'addressLine',
  'addressLine2',
  'city',
  'postCode',
  'county',
];

export function accountantFieldInvalid(form: AccountantForm, field: AccountantTextField): boolean {
  const value = form[field] ?? '';
  return value.length > ACCOUNTANT_MAX_LENGTH[field] || (field === 'name' && value.trim().length === 0);
}

export function accountantFormValid(form: AccountantForm): boolean {
  return ACCOUNTANT_TEXT_FIELDS.every((field) => !accountantFieldInvalid(form, field));
}

function clean(value: string | null | undefined): string | null {
  const trimmed = (value ?? '').trim();
  return trimmed.length > 0 ? trimmed : null;
}

export function accountantPayload(form: AccountantForm, id: string | null): SaveFirmAccountantPayload {
  return {
    id,
    name: (form.name ?? '').trim(),
    addressLine: clean(form.addressLine),
    addressLine2: clean(form.addressLine2),
    city: clean(form.city),
    county: clean(form.county),
    postCode: clean(form.postCode),
    country: clean(form.country),
    accountingBody: clean(form.accountingBody),
    accountingBodyName: isOtherAccountingBody(form.accountingBody) ? clean(form.accountingBodyName) : null,
  };
}
